import React, { useEffect } from 'react';
import SafetyBadge from './SafetyBadge';

function ewgScoreClass(score) {
  if (score <= 2) return { badge: 'bg-primary-100 text-primary-700', label: 'Low' };
  if (score <= 6) return { badge: 'bg-warning/10 text-warning', label: 'Moderate' };
  return { badge: 'bg-error/10 text-error', label: 'High' };
}

function IngredientDrawer({ product, onClose }) {
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const ingredients = Array.isArray(product.ingredients) ? product.ingredients : [];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-neutral-900/40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Ingredients for ${product.name}`}
        className="fixed inset-x-0 bottom-0 z-50 bg-surface-card rounded-t-2xl shadow-xl max-h-[85dvh] flex flex-col md:top-[62px] md:bottom-0 md:right-0 md:left-auto md:w-[420px] md:rounded-none md:rounded-l-2xl md:max-h-full"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-6 border-b border-neutral-200 flex-shrink-0">
          <div className="min-w-0">
            {product.brand && (
              <p className="text-small text-neutral-500 mb-0.5">{product.brand}</p>
            )}
            <h2 className="text-h4 font-semibold text-neutral-900 leading-snug">{product.name}</h2>
            <div className="mt-2">
              <SafetyBadge rating={product.safetyRating} size="sm" />
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex-shrink-0 p-1.5 rounded-md text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors duration-fast focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M4 4l12 12M16 4L4 16" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Ingredient list */}
        <div className="overflow-y-auto flex-1 p-6">
          {ingredients.length === 0 ? (
            <p className="text-body text-neutral-500 text-center py-8">
              No ingredient data available for this product.
            </p>
          ) : (
            <>
              <p className="text-caption font-semibold text-neutral-400 uppercase tracking-caption mb-4">
                {ingredients.length} {ingredients.length === 1 ? 'ingredient' : 'ingredients'}
              </p>
              <ul className="flex flex-col gap-3">
                {ingredients.map((ing, i) => {
                  const { badge, label } = ewgScoreClass(ing.ewgScore ?? 0);
                  return (
                    <li
                      key={i}
                      className="bg-neutral-50 border border-neutral-200 rounded-lg px-4 py-3 flex items-start justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <p className="text-body font-semibold text-neutral-900 leading-snug">{ing.name}</p>
                        {ing.concern && (
                          <p className="text-small text-neutral-500 mt-0.5 leading-snug">{ing.concern}</p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className={`inline-block px-2 py-0.5 rounded-sm text-caption font-bold ${badge}`}>
                          {ing.ewgScore ?? '—'}
                        </span>
                        <span className="text-caption text-neutral-400">{label} risk</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default IngredientDrawer;
